import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { X } from 'lucide-react';
import * as motion from 'motion/react-m';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isUnifiedCollectionsEnabled } from '../../../data/injected';
import { CollectionCapDialog } from './CollectionCapDialog';
import { DocumentsStep } from './DocumentsStep';
import { EntryStep } from './EntryStep';
import {
  type WizardStep,
  type WizardTeamContext,
  useCreateCollectionWizard,
} from './hooks/useCreateCollectionWizard';
import { WebSourcesStep } from './WebSourcesStep';

export interface CreateCollectionWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The team-scoping facts, resolved at the mount from the app's
   * `useTeamEvidenceEnforcement()`. See {@link WizardTeamContext}.
   */
  teamContext: WizardTeamContext;
  /** Which step to start on. Default: 'entry' */
  initialStep?: WizardStep;
  /** When true, the created collection will be scoped to the user's team. */
  defaultTeam?: boolean;
  /** Pre-populate the domain list (e.g. from source recommendations). */
  initialDomains?: string[];
  /** Pre-populate the collection name. */
  initialName?: string;
  /** Override the default "Save" button label (e.g. "Save & Apply"). */
  saveLabel?: string;
  /** Called after any collection type is created.
   *
   * `domainCount` is the count of what was actually persisted (domains
   * for web, documents for docs, or the sum for unified collections) —
   * passed straight through so callers don't have to re-query the
   * created collection just to record analytics.
   *
   * `savedDomains` is the persisted web-source domain list (empty for
   * document-only collections); banner callers diff this against their
   * original recommendation to derive `removed_domains` /
   * `added_domains` on the `SourceRecommendationApplied` event. */
  onCollectionCreated?: (collection: {
    id: string;
    type: 'documents' | 'web';
    domainCount: number;
    savedDomains: string[];
  }) => void;
}

/**
 * Multi-step wizard for creating web source or document collections.
 *
 * - `initialStep="entry"` (default): Evidence flow — shows Step 1 (search + upload)
 * - `initialStep="web-sources"`: Library Web tab flow — jumps straight to Step 2A
 */
export const CreateCollectionWizard = ({
  open,
  onOpenChange,
  initialStep = 'entry',
  defaultTeam = false,
  initialDomains,
  initialName,
  saveLabel,
  onCollectionCreated,
  teamContext,
}: CreateCollectionWizardProps) => {
  const intl = useIntl();

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Snapshot the unified-collections flag at mount so the save-time
  // routing decision can't drift away from the mount-time cap that
  // `useCreateCollectionWizard` captures. If the flag flipped OFF
  // between mount and Save, a 300-domain wizard would otherwise be
  // POSTed to the legacy 100-domain endpoint and the BE would 400.
  const [isUnifiedEnabledAtMount] = useState<boolean>(() =>
    isUnifiedCollectionsEnabled()
  );

  const wizard = useCreateCollectionWizard({
    initialStep,
    teamContext,
    defaultTeam,
    initialDomains,
    initialName,
    onCollectionCreated,
    onClose: handleClose,
  });

  // Reset state on every open/close transition. Resetting on open (not just
  // close) matters because the modal stays mounted while props like
  // `defaultTeam` change — the mount-time wizard state would otherwise be
  // stale on the next open.
  useEffect(() => {
    wizard.reset();
    // Only run on open change, not on wizard reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  return (
    <DialogDrawer open={open} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="w-full max-w-xl overflow-hidden bg-surface-page p-0"
        hideCloseIcon
      >
        <motion.div
          initial={false}
          animate={{ height: wizard.state.step === 'entry' ? 420 : 640 }}
          transition={{ type: 'tween', duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex max-h-[85vh] flex-col overflow-hidden p-6"
        >
          {/* Close button */}
          <Button
            type="button"
            variant="ghost"
            iconOnly
            size="xs"
            className="absolute end-4 top-4"
            onClick={handleClose}
            aria-label={intl.formatMessage({
              defaultMessage: 'Close',
              id: 'UDQZYbSMoQ',
              description:
                'Accessibility label for the button that closes the create collection wizard',
            })}
          >
            <X />
          </Button>

          {/* Step routing */}
          {wizard.state.step === 'entry' && (
            <EntryStep
              onSelectSource={wizard.selectSourceAndTransition}
              onFilesSelected={wizard.stageFilesAndTransition}
              onFilesRejected={wizard.handleFilesRejected}
              error={wizard.error}
            />
          )}

          {wizard.state.step === 'web-sources' && (
            <WebSourcesStep
              name={wizard.displayName}
              onNameChange={wizard.setName}
              addedDomains={wizard.state.addedDomains}
              onAddDomain={wizard.addDomain}
              onAddDomains={wizard.addDomains}
              onRemoveDomain={wizard.removeDomain}
              isTeamCollection={wizard.state.isTeamCollection}
              onTeamCollectionChange={wizard.setIsTeamCollection}
              hasTeam={wizard.hasTeam}
              canCreateTeamCollection={wizard.canCreateTeamCollection}
              teamName={wizard.teamName}
              onBack={wizard.hasEntryStep ? wizard.goBack : undefined}
              onCancel={handleClose}
              // Route to the right endpoint based on the snapshotted
              // unified-collections flag. `saveWebCollection` posts to
              // the legacy `/source-lists/` (100-domain cap);
              // `saveCollection` posts to unified `/evidence/collections`
              // (300-domain cap). Reading the snapshot (vs re-calling
              // `isUnifiedCollectionsEnabled()` here) is critical: if
              // the flag flips OFF after mount, the cap the user sees
              // (300) must still match the endpoint we POST to. Matches
              // the mount-time cap captured by `useCreateCollectionWizard`.
              onSave={() =>
                void (isUnifiedEnabledAtMount
                  ? wizard.saveCollection()
                  : wizard.saveWebCollection())
              }
              isSaving={wizard.isSaving}
              error={wizard.error}
              saveLabel={saveLabel}
              maxDomains={wizard.maxDomains}
            />
          )}

          {wizard.state.step === 'documents' && (
            <DocumentsStep
              name={wizard.displayName}
              onNameChange={wizard.setName}
              stagedFiles={wizard.state.stagedFiles}
              onAddFiles={wizard.addFiles}
              onRemoveFile={wizard.removeFile}
              selectedExistingDocs={wizard.state.selectedExistingDocs}
              onAddExistingDoc={wizard.addExistingDocument}
              onRemoveExistingDoc={wizard.removeExistingDocument}
              isTeamCollection={wizard.state.isTeamCollection}
              onTeamCollectionChange={wizard.setIsTeamCollection}
              hasTeam={wizard.hasTeam}
              canCreateTeamCollection={wizard.canCreateTeamCollection}
              teamName={wizard.teamName}
              onBack={wizard.hasEntryStep ? wizard.goBack : undefined}
              onCancel={handleClose}
              onSave={() => void wizard.saveDocumentCollection()}
              isSaving={wizard.isSaving}
              error={wizard.error}
            />
          )}
        </motion.div>
      </DialogDrawerContent>
      <CollectionCapDialog
        open={wizard.isCapDialogOpen}
        onOpenChange={(o) => !o && wizard.dismissCapDialog()}
        maxDomains={wizard.maxDomains}
      />
    </DialogDrawer>
  );
};
